import React, { useEffect, useRef, useState } from "react";
import { fnGetMaxOrderLimitNo } from "../utilities/Common";

type QtySelectorProps = {
  value: number;                 // backend cart qty
  availableStock: number;        // backend free stock
  disabled?: boolean;
  onChange: (qty: number) => Promise<void> | void;
};

const QtySelector: React.FC<QtySelectorProps> = ({
  value,
  availableStock,
  disabled,
  onChange,
}) => {
  const [qty, setQty] = useState<number>(value || 1);
  const [isUpdating, setIsUpdating] = useState(false); // ✅ UI loading state

  const isUpdatingRef = useRef(false);
  const lastBackendQtyRef = useRef<number>(value || 1);

  const ORDER_LIMIT = fnGetMaxOrderLimitNo();

  // total stock = frozen(cart) + free(stock)
  const totalStock = (value || 0) + availableStock;

  // dropdown max rule
  const dropdownMax = Math.min(ORDER_LIMIT, totalStock);

  // Backend → UI sync (race-condition safe)
  useEffect(() => {
    if (isUpdatingRef.current) return;

    let backendQty = value || 1;

    // clamp only by order limit
    if (backendQty > ORDER_LIMIT) {
      backendQty = ORDER_LIMIT;
    }

    if (backendQty !== lastBackendQtyRef.current) {
      setQty(backendQty);
      lastBackendQtyRef.current = backendQty;
    }
  }, [value, ORDER_LIMIT]);

  const handleChange = async (newQty: number) => {
    if (newQty === qty) return;

    setQty(newQty);

    isUpdatingRef.current = true;
    setIsUpdating(true);
    lastBackendQtyRef.current = newQty;

    try {
      await onChange(newQty);
    } catch (err) {
      console.error("Quantity update failed", err);
    } finally {
      isUpdatingRef.current = false;
      setIsUpdating(false);
    }
  };

  const isOutOfStock = totalStock <= 0;
  const isBackOrder = qty > availableStock;

  return (
    <div className="d-inline-flex align-items-center gap-2">
      <select
        value={qty}
        disabled={disabled || isOutOfStock || isUpdating}   // ✅ LOCKED during API
        onChange={(e) => handleChange(Number(e.target.value))}
        className="form-select form-select-sm"
      >
        {Array.from({ length: dropdownMax }, (_, i) => i + 1).map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
    </div>
  );
};

export default QtySelector;
